import React from "react";

function parseBold(text) {
  const parts = (text || '').split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part);
}
import { Prompt } from "react-router-dom";
import { connect } from "react-redux";
import { API_ROOT } from "../config";
import TrainingPageHeader from "../components/TrainingPageHeader";
import { PenSquare } from "lucide-react";
import "../styles/TrainingTestSection.css";

class TrainingTestSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      formation: null,
      block: null,
      loading: true,
      error: null,
      answers: {},
      submitted: false,
      score: null,
      dirty: false,
      blockAlreadyApproved: false,
      previousScore: null,
      instructionsOpen: false,
    };
    this.handleBeforeUnload = (e) => {
      if (!this.state.dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    this.loadData();
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }

  async loadData() {
    const { userId, formationSlug, blockId } = this.props.match.params;
    const token = localStorage.getItem("token");
    try {
      const [formationRes, progressRes] = await Promise.all([
        fetch(`${API_ROOT}formations/${formationSlug}`, {
          headers: { Authorization: `Token ${token}` },
        }),
        fetch(`${API_ROOT}promoters/promoter/${userId}/formations`, {
          headers: { Authorization: `Token ${token}` },
        }),
      ]);
      if (!formationRes.ok) throw new Error("Error al cargar la formación");
      const formation = await formationRes.json();
      const block =
        (formation.blocks || []).find((b) => b._id === blockId) || null;

      let blockAlreadyApproved = false;
      let previousScore = null;
      if (progressRes.ok) {
        const progressData = await progressRes.json();
        const entry = (progressData || []).find(
          (e) =>
            e.formation?._id === formation._id || e.formation === formation._id,
        );
        if (entry) {
          const bp = (entry.blocks || []).find(
            (b) => b.block === blockId || b.block?._id === blockId,
          );
          if (bp?.approved) {
            blockAlreadyApproved = true;
            previousScore = bp.score ?? null;
          }
        }
      }

      this.setState({
        formation,
        block,
        loading: false,
        dirty: !blockAlreadyApproved,
        blockAlreadyApproved,
        previousScore,
      });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  }

  handleAnswer(sectionId, idx) {
    if (this.state.submitted) return;
    this.setState((prev) => ({
      answers: { ...prev.answers, [sectionId]: idx },
    }));
  }

  async handleSubmit() {
    const { block, answers, formation } = this.state;
    const { userId, blockId } = this.props.match.params;
    const quizSections = (block.sections || []).filter(
      (s) => s.type === "quiz",
    );
    let total = 0;
    let correct = 0;
    quizSections.forEach((section) => {
      const isPenalized = section.quizContent?.penalized;
      (section.quizContent?.questions || []).forEach((q, qIdx) => {
        total++;
        const key = `${section._id}-${qIdx}`;
        const correctIdx = (q.options || []).findIndex((o) => o.correct);
        if (answers[key] === correctIdx) correct++;
        if (answers[key] === undefined) total--;
        if (answers[key] !== undefined && correctIdx === -1) total--;
        if (
          isPenalized &&
          answers[key] !== undefined &&
          correctIdx !== -1 &&
          answers[key] !== correctIdx
        )
          correct--;
      });
    });
    if (total === 0) return;
    const score = Math.round((correct / total) * 100);
    this.setState({ submitted: true, score, dirty: false });

    // Guardar resultado en el servidor
    try {
      const token = localStorage.getItem("token");
      await fetch(
        `${API_ROOT}promoters/${userId}/formations/${formation._id}/blocks/${blockId}/approve`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ score }),
        },
      );
    } catch (err) {
      console.error("Error al guardar la aprobación:", err);
    }
  }

  render() {
    const {
      formation,
      block,
      loading,
      error,
      answers,
      submitted,
      score,
      dirty,
      blockAlreadyApproved,
      previousScore,
      instructionsOpen,
    } = this.state;
    const { userId } = this.props.match.params;
    const isAdmin = this.props.role === 'ADMIN';

    if (loading) return <div className="p-4">Cargando...</div>;
    if (error) return <div className="p-4 text-danger">{error}</div>;
    if (!block)
      return <div className="p-4 text-muted">Bloque no encontrado.</div>;

    const quizSections = (block.sections || []).filter(
      (s) => s.type === "quiz",
    );
    const penalized = quizSections.some((s) => s.quizContent?.penalized);
    const passThreshold = block.pass || 50;
    const passed = submitted && score >= passThreshold;
    const isReadOnly = blockAlreadyApproved;
    
    const desmarcarPregunta = (key) => {
      this.setState(prev => {
        const next = { ...prev.answers };
        delete next[key];
        return { answers: next };
      });
    };

    return (
      <>
      <TrainingPageHeader icon={<PenSquare size={30} />} title={block.name} />
      <div className="test-left-section">
        <Prompt
          when={dirty && !isAdmin}
          message="¿Seguro que quieres salir? Perderás el progreso no guardado."
        />

        

        <div className="exam-section p-x-3 bg-white rounded shadow-sm flex-3">
          <div className="exam-border"></div>
          <div className="exam-container">
            <div className="exam-title-container mb-2">
              <h5 className="mb-1">{formation.name}</h5>
              <h6 className="mb-0 text-muted">
                {block.name} — Aprobado con {passThreshold}% o más
              </h6>
            </div>

            {isReadOnly && (
              <div className="alert alert-success mb-3">
                <i className="fas fa-circle-check me-2" />
                <strong>Ya aprobado</strong>
                {previousScore !== null && ` — Puntuación: ${previousScore}%`}
              </div>
            )}

            {quizSections.length === 0 && (
              <div className="text-muted">
                Este bloque no tiene preguntas de test.
              </div>
            )}

            {quizSections.map((section) => (
              <div key={section._id}>
                {(section.quizContent?.questions || []).map((q, qIdx) => {
                const key = `${section._id}-${qIdx}`;
                const correctIdx = (q.options || []).findIndex(
                  (o) => o.correct,
                );
                const showFeedback = submitted || isReadOnly;
                return (
                  <div key={key} id={key} className="answers-container mb-4">
                    <div className="question-container d-flex justify-content-between align-items-start">
                      <h2 className="font-size-12">
                        <strong>{qIdx + 1}.</strong> {parseBold(q.question)}
                      </h2>
                      {!submitted && !isReadOnly && answers[key] !== undefined && (
                        <button
                          className="btn btn-sm btn-outline-secondary ms-2 flex-shrink-0"
                          onClick={() => desmarcarPregunta(key)}
                        >
                          Desmarcar
                        </button>
                      )}
                    </div>
                    <div className="Preguntas">
                      {(q.options || []).map((opt, optIdx) => (
                        <label
                          key={optIdx}
                          className="d-flex align-items-center gap-2 mb-2"
                          style={{
                            cursor:
                              submitted || isReadOnly ? "default" : "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name={key}
                            value={optIdx}
                            checked={
                              isReadOnly
                                ? optIdx === correctIdx
                                : answers[key] === optIdx
                            }
                            onChange={() => this.handleAnswer(key, optIdx)}
                            disabled={submitted || isReadOnly}
                          />
                          {parseBold(opt.text)}
                        </label>
                      ))}
                    </div>
                    {showFeedback &&
                      !isReadOnly &&
                      (() => {
                        const isCorrect = answers[key] === correctIdx;
                        return (
                          <div
                            className={`alert ${isCorrect ? "alert-success" : "alert-danger"}`}
                          >
                            {!isCorrect && (
                              <span className="ms-2">
                                Respuesta correcta:{" "}
                                {(q.options || []).find((o) => o.correct)
                                  ?.text || "N/A"}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                  </div>
                );
              })}
              </div>
            ))}

            {isAdmin ? (
              <button
                className="btn btn-outline-primary mt-3"
                onClick={() => this.props.history.push(`/training/${userId}`)}
              >
                <i className="fas fa-arrow-left me-2" />Volver a formaciones
              </button>
            ) : (!submitted && !isReadOnly && quizSections.length > 0 && (
              <button
                className="btn btn-primary"
                onClick={() => this.handleSubmit()}
              >
                Enviar respuestas
              </button>
            ))}

            {(submitted || isReadOnly) && (
              <>
                {submitted && (
                  <div
                    className={`alert mt-3 ${passed ? "alert-success" : "alert-danger"}`}
                  >
                    <strong>{passed ? "¡Aprobado!" : "Suspenso"}</strong> —
                    Puntuación: {score}%
                    {!passed && (
                      <span className="ms-2">(mínimo {passThreshold}%)</span>
                    )}
                  </div>
                )}
                <button
                  className="finish-button"
                  onClick={() => (window.location.href = `/training/${userId}`)}
                >
                  <span className="finish-text">Volver a la formación</span>
                </button>
              </>
            )}
          </div>
        </div>
        <div className=" test-right-section flex-1">

              {quizSections.length > 0 && (
                <>
                  <div className="instructions-section">
                    <div className="instructions-border"></div>
                    <div className="instructions-container">
                      <button
                        className="instructions-toggle"
                        onClick={() => this.setState(prev => ({ instructionsOpen: !prev.instructionsOpen }))}
                      >
                        <span>Instrucciones para el examen</span>
                        <i className={`fas fa-chevron-down ms-2 ${instructionsOpen ? 'instructions-toggle--open' : ''}`} />
                      </button>
                      {instructionsOpen && (
                        <>
                          <div className="alert alert-info mt-2">
                            <i className="fas fa-info-circle me-2" />
                            Responde todas las preguntas y pulsa "Enviar respuestas" para
                            completar el bloque.
                          </div>
                          {penalized && (
                            <div className="alert alert-warning">
                              <i className="fas fa-triangle-exclamation me-2" />
                              Ten cuidado, respuestas incorrectas penalizan tu puntuación
                              final.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <h2 className="navegacion-rapida-title">
                    <i className="fas fa-list-ol me-2"></i>
                    Navegación rápida
                  </h2>
                  {(() => {
                    let globalIdx = 0;
                    return quizSections.map((section) => (
                      <div key={section._id} className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted fw-semibold">{section.name || "Sección"}</small>
                        </div>
                        <div className="instructions-section p-0 flex-wrap">
                          {(section.quizContent?.questions || []).map((_, qIdx) => {
                            const id = `${section._id}-${qIdx}`;
                            const label = ++globalIdx;
                            return (
                              <button
                                key={id}
                                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                                className={`go-to-question ${answers[id] !== undefined ? "go-to-question--answered" : ""}`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                  
                </>
              )}
        </div>
      </div>
      </>
    );
  }
}

export default connect(state => ({ role: state.auth.role }))(TrainingTestSection);
